<template>
    <div class="flex flex-col">

        <div class="flex flex-row flex-1 space-x-3 mb-2">
            <!-- Search Factors -->
            <div class="flex flex-col flex-1">
                <label class="block text-xs font-medium text-gray-700 mb-1">Search Factors</label>
                <VueMultiselect  v-model="selectedKeywords" :options="groupedOptions" :multiple="true"
                    :close-on-select="false" :clear-on-select="false" :preserve-search="true" group-values="items"
                    group-label="category" :group-select="false" label="name" track-by="value" :searchable="true"
                    :show-no-results="true" :clearable="false" :show-labels="false" :max-height="200"
                    class="multiselect-blue w-full" @input="applyFilters" @remove="onTagRemove">
                    <template #noResult>
                        <div class="text-xs text-gray-500 p-2">No matching factors found</div>
                    </template>
                </VueMultiselect>
            </div>

            <!-- Keyword Search -->
            <div class="flex flex-col flex-1">
                <label for="keyword-search" class="block text-xs font-medium text-gray-700 mb-1">Keyword Search</label>
                <div class="relative w-full">
                    <input type="text" id="keyword-search" v-model.trim="keywordSearch" @input="applyFilters"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                </div>
            </div>
        </div>
        <!-- Date Range Filter -->
        <div class="flex flex-col">
            <label class="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
            <div class="flex items-center space-x-2">
                <select v-model="selectedRange" @change="onRangeChange"
                    class="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm">
                    <option value="">Select a range</option>
                    <option v-for="range in dateRanges" :key="range.value" :value="range.value">
                        {{ range.label }}
                    </option>
                    <option value="custom">Custom</option>
                </select>
                <!-- Date Range Display -->
                <div v-if="selectedRange === 'custom'" class="flex items-center space-x-2">
                    <div>
                        <input type="date" v-model="customStartDate" @change="applyFilters"
                            class="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <span>to</span>
                    <div>
                        <input type="date" v-model="customEndDate" @change="applyFilters"
                            class="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                </div>
                <div v-else class="flex items-center">
                    <span class="text-sm">{{ getSelectedRangeDisplay() }}</span>
                </div>
            </div>
        </div>

    </div>
</template>

<style scoped>
/* Popover styles */
.popover-enter-active,
.popover-leave-active {
    transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out;
}

.popover-enter-from,
.popover-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Custom Multiselect styles */
:deep(.multiselect-blue .multiselect__tag) {
    background-color: #dbeafe;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
    margin-right: 0.25rem;
}

:deep(.multiselect-blue .multiselect__tag-icon:focus),
:deep(.multiselect-blue .multiselect__tag-icon:hover) {
    background-color: #bfdbfe;
    color: #1e40af;
}

:deep(.multiselect-blue .multiselect__option--highlight) {
    background-color: #dbeafe;
    color: #1e40af;
}

:deep(.multiselect-blue .multiselect__option--selected) {
    background-color: #eff6ff;
    color: #1e40af;
    font-weight: 500;
}

:deep(.multiselect-blue .multiselect__option--selected.multiselect__option--highlight) {
    background-color: #dbeafe;
    color: #1e40af;
}

:deep(.multiselect-blue .multiselect__tags) {
    border-color: #d1d5db;
    border-radius: 0.375rem;
    min-height: 42px;
    max-height: 70px;
    overflow-y: auto;
}

:deep(.multiselect-blue .multiselect__content-wrapper) {
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

:deep(.multiselect-blue .multiselect__input) {
    font-size: 0.875rem;
    border: 0;
    box-shadow: none;
}

:deep(.multiselect-blue .multiselect__input:focus) {
    box-shadow: none;
    outline: none;
}

:deep(.multiselect-blue .multiselect__placeholder) {
    color: #9ca3af;
    font-size: 0.875rem;
    margin-bottom: 0;
    margin-top: 0.125rem;
}

/* Make group headers visible and prevent hover/keyboard highlight styles */
:deep(.multiselect-blue .multiselect__option--group) {
    background-color: #f3f4f6 !important;
    /* Tailwind bg-gray-100 */
    color: #374151 !important;
    /* Tailwind text-gray-700 */
    font-weight: 600;
    padding: 6px 8px;
    pointer-events: none;
    /* not clickable since group-select=false */
}

/* Ensure highlight state doesn't override group header colors */
:deep(.multiselect-blue .multiselect__option--group.multiselect__option--highlight) {
    background-color: #f3f4f6 !important;
    color: #374151 !important;
}

/* Make sure text isn’t clipped if theme sets inline styles */
:deep(.multiselect-blue .multiselect__option--group span) {
    display: block;
}
</style>

<script>
import { mapState, mapMutations } from 'vuex';
import VueMultiselect from 'vue-multiselect';

// Moved out to avoid recreating on each component instance render
const SEARCH_FACTORS = {
    'Geopolitical / Conflict-Related': [
        'Gaza', 'Israel', 'Palestinian', 'Liberation', 'Ceasefire', 'Genocide',
        'Occupation', 'Apartheid', 'Zionism', 'Imperialism', 'Hamas', 'Netanyahu'
    ],
    'Advocacy & Justice': [
        'Justice', 'Accountability', 'War crimes', 'Freedom', 'Equality',
        'Human rights', 'Civil liberties', 'Democracy', 'Solidarity'
    ],
    'U.S. Policy & Politics': [
        'Trump', 'Biden', 'Congress', 'Aid', 'Weapons', 'Military',
        'Sanctions', 'Embargo', 'Foreign policy', 'U.S. support'
    ],
    'Social Movements': [
        'Black Lives Matter', 'Racism', 'Police brutality', 'Abolishing police',
        'White nationalism', 'Christian values', 'LGBTQ+ rights', 'Trans rights', 'Reproductive rights'
    ],
    'Environmental & Climate': [
        'Climate action', 'Fossil fuels', 'Chevron', 'Environmental justice',
        'Animal cruelty', 'Factory farming'
    ]
};

export default {
    name: 'ProtestFilters',
    components: { VueMultiselect },

    data() {
        return {
            selectedKeywords: [],
            keywordSearch: '',
            selectedRange: '',
            showDatePicker: false,
            customStartDate: this.formatDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
            customEndDate: this.formatDate(new Date()),
            dateRanges: [
                { label: '30 days', value: '30' },
                { label: '60 days', value: '60' },
                { label: '90 days', value: '90' },
                { label: '120 days', value: '120' }
            ],
        };
    },

    computed: {
        ...mapState({
            protestFilters: state => state.reatlas?.protestFilters || {
                dateRange: '30',
                startDate: this.formatDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
                endDate: this.formatDate(new Date()),
                selectedFactors: [],
                searchText: ''
            }
        }),
        groupedOptions() {
            return Object.entries(SEARCH_FACTORS).map(([category, keywords]) => ({
                category,
                items: keywords.map(keyword => ({
                    name: keyword,
                    value: `${category}:${keyword}`
                }))
            }));
        }
    },

    created() {
        // Initialize dates from store or use defaults
        if (this.protestFilters.dateRange) {
            this.selectedRange = this.protestFilters.dateRange;
        }
        if (this.protestFilters.startDate) {
            this.customStartDate = this.protestFilters.startDate;
        }
        if (this.protestFilters.endDate) {
            this.customEndDate = this.protestFilters.endDate;
        }
    },

    watch: {
        // Keep local UI in sync when store changes elsewhere
        protestFilters: {
            immediate: true,
            handler(newVal) {
                if (!newVal) return;
                this.keywordSearch = newVal.searchText || '';
                this.selectedKeywords = (newVal.selectedFactors || []).map(v => {
                    const name = (typeof v === 'string' && v.includes(':')) ? v.split(':')[1] : v;
                    return { name, value: typeof v === 'string' ? v : v?.value || name };
                });
            }
        }
    },

    methods: {
        ...mapMutations(['setProtestFilters']),
        
        onTagRemove(removedTag) {
            console.log('Tag removed:', removedTag);
            console.log('Remaining tags:', this.selectedKeywords);
            if (window.protestPopupClose) window.protestPopupClose();
        },

        applyFilters() {
            let startDate = this.customStartDate;
            let endDate = this.customEndDate;

            if(this.selectedRange ==='custom') {
                startDate = this.formatDate(new Date(this.customStartDate));
                endDate = this.formatDate(new Date(this.customEndDate));
            }
            const filters = {
                selectedFactors: this.selectedKeywords.map(k =>
                    typeof k === 'object' ? (k.value || k.name) : k
                ),
                searchText: this.keywordSearch || '',
                dateRange: this.selectedRange ? {
                    startDate,
                    endDate
                } : null,
                dateRangeValue: this.selectedRange
            };

            this.setProtestFilters({
                ...this.protestFilters,
                ...filters
            });

            if (window.applyProtestMapFilters) {
                window.applyProtestMapFilters(filters);
            }
            if (window.protestPopupClose) window.protestPopupClose();

            this.$emit('filters-updated');
        },

        clearFilters() {
            this.selectedKeywords = [];
            this.keywordSearch = '';
            this.selectedRange = '';
            this.customStartDate = '';
            this.customEndDate = '';

            this.setProtestFilters({
                dateRange: '',
                startDate: '',
                endDate: '',
                selectedFactors: [],
                keyword: '',
            });
            
            // Apply the cleared filters
            this.applyFilters();
            if (window.protestPopupClose) window.protestPopupClose();
        },

        onRangeChange() {
            if (this.selectedRange === '') {
                // Clear date range
                this.applyFilters();
            } else if (this.selectedRange !== 'custom') {
                const days = parseInt(this.selectedRange);
                const endDate = new Date();
                const startDate = new Date();
                startDate.setDate(endDate.getDate() - days);

                this.customStartDate = this.formatDate(startDate);
                this.customEndDate = this.formatDate(endDate);

                this.applyFilters();
            }
        },

        togglePopup() {
            this.showDatePicker = !this.showDatePicker;
        },

        closePopup() {
            this.showDatePicker = false;
        },

        applyCustomDates() {
            if (this.selectedRange === 'custom') {
                this.applyFilters();
                this.closePopup();
            }
        },

        formatDateForDisplay(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}-${day}-${year}`;
        },

        getSelectedRangeDisplay() {
            if (!this.selectedRange) return '';

            if (this.selectedRange === 'custom') {
                return `${this.formatDateForDisplay(this.customStartDate)} - ${this.formatDateForDisplay(this.customEndDate)}`;
            }

            // For predefined ranges
            const days = parseInt(this.selectedRange);
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(endDate.getDate() - days);

            return `${this.formatDateForDisplay(startDate)} - ${this.formatDateForDisplay(endDate)}`;
        },


        formatDate(date) {
            const d = new Date(date);
            let month = '' + (d.getMonth() + 1);
            let day = '' + d.getDate();
            const year = d.getFullYear();

            if (month.length < 2) month = '0' + month;
            if (day.length < 2) day = '0' + day;

            return [month, day, year].join('-');
        }
    }
};
</script>